import { Fragment, useCallback } from 'react';
import { CSSTransition } from 'react-transition-group';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import cards from '~/assets/icons/cards.svg';
import cash from '~/assets/icons/cash.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CurrencyIcon } from '~/components/collections';
import { useWalletIcon } from '~/hooks';
import { WalletType, walletTypeCurrency } from '~/shared';
import { Button, ButtonColors, ButtonHeight } from '../Button';
import { Divider } from '../Divider';
import { SelectDiscount } from '../SelectDiscount';
import { Text } from '../Text';
import { IShopPreviewProps, VariationMode } from './ShopPreview.model';
import {
	StyledButton,
	StyledButtonContainer,
	StyledButtonContent,
	StyledButtonsContainer,
	StyledContainer,
	StyledName,
	StyledOldPriceContainer,
	StyledPriceContainer,
	StyledRow,
	StyledStockRow,
	StyledVariants,
	StyledVariantsContainer,
	StyledVariantsRow,
} from './ShopPreview.styled';

const ShopPreview = ({
	name,
	price,
	subtitle,
	description,
	handlePayByCash,
	handlePayByCard,
	handlePayByDonate,
	visible,
	walletType = WalletType.Cash,
	variationTitle,
	variationMode,
	variationIndex,
	discount,
	oldPrice,
	onVariationChange,
	maxVariantIndex = 1,
	children,
	customButtons,
	bottomTitle,
	selectDiscount,
	stock,
	...props
}: IShopPreviewProps &
	React.HTMLAttributes<HTMLDivElement> &
	React.PropsWithChildren) => {
	const currencyIcon = useWalletIcon(walletTypeCurrency[walletType], true);

	const handlePrevVariant = useCallback(
		(e: React.MouseEvent) => {
			if (maxVariantIndex === 1 || variationIndex === undefined) return;

			const newIndex = e.shiftKey
				? Math.max(0, variationIndex - 10)
				: variationIndex === 0
				? maxVariantIndex - 1
				: variationIndex - 1;

			if (newIndex === variationIndex) return;

			playSound(Sound.ClickButton);
			onVariationChange?.(newIndex);
		},
		[variationIndex, maxVariantIndex, onVariationChange],
	);

	const handleNextVariant = useCallback(
		(e: React.MouseEvent) => {
			if (maxVariantIndex === 1 || variationIndex === undefined) return;

			const lastIndex = maxVariantIndex - 1;
			const newIndex = e.shiftKey
				? Math.min(lastIndex, variationIndex + 10)
				: variationIndex === lastIndex
				? 0
				: variationIndex + 1;

			if (newIndex === variationIndex) return;

			playSound(Sound.ClickButton);
			onVariationChange?.(newIndex);
		},
		[variationIndex, maxVariantIndex, onVariationChange],
	);

	return (
		<CSSTransition
			timeout={300}
			classNames={'shop-preview'}
			in={visible}
			unmountOnExit
			appear
		>
			<StyledContainer {...props}>
				<StyledName>
					{name} {subtitle && <span>{subtitle}</span>}
				</StyledName>
				<Divider $direction={'H'} style={{ marginTop: 16, opacity: 0.1 }} />
				{description && (
					<Fragment>
						<Text
							style={{
								display: 'block',
								fontSize: 16,
								lineHeight: '18px',
								color: 'rgba(255,255,255,0.5)',
								marginTop: 20,
							}}
						>
							{description}
						</Text>
						<Divider $direction={'H'} style={{ marginTop: 20, opacity: 0.1 }} />
					</Fragment>
				)}
				{children}
				{variationIndex !== undefined && maxVariantIndex > 1 && (
					<StyledVariantsRow>
						<Text>{variationTitle}</Text>
						<StyledVariantsContainer>
							<StyledButtonContainer
								onClick={(e) => handlePrevVariant(e)}
								onMouseEnter={() => playSound(Sound.LinkCursor)}
								style={{ borderRadius: '5px 0 0 5px' }}
							>
								<img src={arrowLeft} />
							</StyledButtonContainer>
							<StyledVariants>
								<Text>
									<span>{variationIndex + 1}</span>
									{variationMode === VariationMode.Variant && (
										<Fragment>/{maxVariantIndex}</Fragment>
									)}
								</Text>
							</StyledVariants>
							<StyledButtonContainer
								onClick={(e) => handleNextVariant(e)}
								onMouseEnter={() => playSound(Sound.LinkCursor)}
								style={{ borderRadius: '0 5px 5px 0' }}
							>
								<img src={arrowLeft} style={{ transform: 'rotate(180deg)' }} />
							</StyledButtonContainer>
						</StyledVariantsContainer>
					</StyledVariantsRow>
				)}
				{discount ? (
					<StyledRow>
						<Text style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>
							Скидка
						</Text>
						<Text style={{ fontSize: 16, fontWeight: 700, color: '#ffffff' }}>
							{discount}%
						</Text>
					</StyledRow>
				) : null}
				{stock ? (
					<StyledStockRow>
						<Text style={{ opacity: 0.5 }}>В наличии</Text>
						<Text>
							<span style={{ fontWeight: 700 }}>{stock}</span>
							<span style={{ opacity: 0.5 }}>&nbsp;шт.</span>
						</Text>
					</StyledStockRow>
				) : null}
				<StyledPriceContainer $oldPrice={!!oldPrice} $hide={props.hide?.price}>
					<Text style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)' }}>
						Стоимость
					</Text>
					{oldPrice && (
						<StyledOldPriceContainer>
							<Text>{oldPrice?.toLocaleString('ru-RU')}</Text>
							<CurrencyIcon
								icon={currencyIcon}
								size="sm"
								style={{ opacity: walletType === WalletType.Donate ? 1 : 0.5 }}
							/>
							<div className="line"></div>
						</StyledOldPriceContainer>
					)}
					<Text style={{ fontSize: 16, fontWeight: 700, color: '#ffffff' }}>
						{price.toLocaleString('ru-RU')}
					</Text>
					<CurrencyIcon
						icon={currencyIcon}
						size="md"
						style={{ opacity: walletType === WalletType.Donate ? 1 : 0.5 }}
					/>
				</StyledPriceContainer>
				{selectDiscount && (
					<div style={{ marginTop: 20 }}>
						<SelectDiscount
							items={selectDiscount.items}
							value={selectDiscount.value}
							onChange={selectDiscount.onChange}
						/>
					</div>
				)}
				{walletType === WalletType.Donate ? (
					<Button
						color={ButtonColors.Buy}
						height={ButtonHeight.Height40}
						onClick={handlePayByDonate}
						style={{
							marginTop: 30,
							display: props.hide?.buttons ? 'none' : 'inherit',
						}}
					>
						<Text>Купить</Text>
					</Button>
				) : (
					<StyledButtonsContainer
						style={{ marginTop: description ? 20 : selectDiscount ? 20 : 30 }}
						$hide={props.hide?.buttons}
					>
						{customButtons?.map((button, index) => {
							const isLastOdd =
								customButtons.length % 2 !== 0 &&
								index === customButtons.length - 1;

							return (
								<StyledButton
									color={button.color}
									height={ButtonHeight.Height40}
									disabled={button.disabled}
									onClick={(e) => {
										e.currentTarget.blur();
										button.onClick();
									}}
									key={index}
									$isLastOdd={isLastOdd}
								>
									<StyledButtonContent>
										{button?.icon && <img src={button.icon} />}
										<Text>{button.text}</Text>
									</StyledButtonContent>
								</StyledButton>
							);
						})}
						{!customButtons?.length && (
							<Fragment>
								<Button
									color={ButtonColors.Default}
									height={ButtonHeight.Height40}
									onClick={(e) => {
										e.currentTarget.blur();
										handlePayByCard?.();
									}}
								>
									<StyledButtonContent>
										<img src={cards} />
										<Text>Картой</Text>
									</StyledButtonContent>
								</Button>
								<Button
									color={ButtonColors.Buy}
									height={ButtonHeight.Height40}
									onClick={(e) => {
										e.currentTarget.blur();
										handlePayByCash?.();
									}}
								>
									<StyledButtonContent>
										<img src={cash} />
										<Text>Наличными</Text>
									</StyledButtonContent>
								</Button>
							</Fragment>
						)}
					</StyledButtonsContainer>
				)}
				{bottomTitle && (
					<Fragment>
						<Divider
							$direction={'H'}
							style={{ marginTop: description ? 30 : 20, opacity: 0.1 }}
						/>
						<Text
							style={{
								fontSize: 14,
								color: 'rgba(255,255,255,0.25)',
								textAlign: 'center',
								marginTop: 20,
							}}
						>
							{bottomTitle}
						</Text>
					</Fragment>
				)}
			</StyledContainer>
		</CSSTransition>
	);
};

export { ShopPreview };
